/**
 * CreatorRow.js
 *
 * Displays creator/user information for cards.
 * Shows avatar, name, timestamp, and type badge.
 * Used in Discover context to show who created the content.
 */

import React, { memo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../shared/Icon';
import { isImageSrc } from '../../utils/imageSource';
import FriendsServiceDB from '../../services/friendsServiceDB';
import { invalidateUserContentCaches } from '../../utils/cacheInvalidation';
import { focusStyle } from '../../styles/focus';

// Every card by the same creator flips to "Following" together.
const CREATOR_FOLLOWED_EVENT = 'creator-followed';

/**
 * User avatar component with fallback. The initials fallback is a flat tint
 * since the 2026-10-01 refresh; `gradientClass` is still accepted from callers
 * but no longer drawn.
 */
// eslint-disable-next-line no-unused-vars
const UserAvatar = ({ avatar, name, gradientClass }) => {
  const { t } = useTranslation('feedCard');

  if (isImageSrc(avatar)) {
    return (
      <img
        src={avatar}
        alt=""
        className="w-8 h-8 rounded-full object-cover"
      />
    );
  }

  return (
    <div className="w-8 h-8 bg-primary-lighter rounded-full flex items-center justify-center">
      <span className="text-primary-dark font-semibold text-xs">
        {name?.charAt(0).toUpperCase() || t('creator.avatarInitialFallback')}
      </span>
    </div>
  );
};

/**
 * Type badge component
 */
const TypeBadge = ({ badge }) => {
  if (!badge) return null;

  return (
    <div className={`flex items-center space-x-1 text-xs px-2 py-1 rounded-full ${badge.bgClass} ${badge.textClass}`}>
      {badge.icon && <Icon name={badge.icon} className="w-3.5 h-3.5" />}
      <span>{badge.label}</span>
    </div>
  );
};

/**
 * One-tap Follow for a stranger's post (docs/plans/DISCOVER_REDESIGN.md §6:
 * easy following). No unfollow here: that stays on the profile.
 */
const FollowButton = ({ userId, name }) => {
  const { t } = useTranslation('feedCard');
  const [state, setState] = useState('idle'); // idle | pending | following | failed

  useEffect(() => {
    const onFollowed = (e) => { if (e.detail?.userId === userId) setState('following'); };
    window.addEventListener(CREATOR_FOLLOWED_EVENT, onFollowed);
    return () => window.removeEventListener(CREATOR_FOLLOWED_EVENT, onFollowed);
  }, [userId]);

  const follow = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (state === 'pending' || state === 'following') return;
    setState('pending');
    const result = await FriendsServiceDB.followUser(userId);
    // "Already following" means this card's flag was stale (a buffered mix
    // page, a cache restore, another tab): show the truth, as SharedTrip does.
    const already = result?.error === 'Already following this user';
    if (result?.success || already) {
      setState('following');
      window.dispatchEvent(new CustomEvent(CREATOR_FOLLOWED_EVENT, { detail: { userId } }));
      // The follow graph changed: cached feeds and Home strips refetch.
      invalidateUserContentCaches();
    } else {
      setState('failed');
    }
  };

  const displayName = name || t('creator.nameFallback');
  if (state === 'following') {
    return (
      <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium text-content-secondary px-1 min-h-[44px] sm:min-h-0" role="status">
        <Icon name="check" className="w-3.5 h-3.5" />
        {t('creator.following')}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={follow}
      disabled={state === 'pending'}
      aria-label={state === 'failed' ? t('creator.followFailedAria', { name: displayName }) : t('creator.followAria', { name: displayName })}
      className={`flex-shrink-0 px-2 min-h-[44px] sm:min-h-[32px] text-sm font-bold text-primary hover:text-primary-dark disabled:opacity-60 rounded ${focusStyle}`}
    >
      {state === 'failed' ? t('creator.followFailed') : t('creator.follow')}
    </button>
  );
};

/**
 * Main CreatorRow Component
 *
 * Props:
 * @param {Object} creator - Creator data { id, name, avatar, isOwn }
 * @param {string} timeAgo - Formatted time string
 * @param {Object} badge - Type badge { icon, label, bgClass, textClass }
 * @param {boolean} showBadge - Whether to show the type badge
 * @param {boolean} showFollow - One-tap Follow when creator.canFollow (Discover only)
 * @param {string} avatarGradient - Tailwind gradient classes for fallback avatar
 */
const CreatorRow = ({
  creator,
  timeAgo,
  badge,
  showBadge = true,
  showFollow = false,
  avatarGradient = 'from-primary to-primary-dark'
}) => {
  const { t } = useTranslation('feedCard');

  if (!creator) return null;

  const profileUrl = creator.isOwn ? '/profile' : `/profile/${creator.id}`;

  // The row wraps: when the name and the actions don't fit on one line (a
  // 260px Discover shelf card with Follow AND the type badge squeezed the
  // name to "Kurtis …"), the actions drop to a second line, still on the
  // right, and the name keeps the full width. Full-width cards stay on one.
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 mb-3 min-w-0">
      <Link
        to={profileUrl}
        className="flex items-center space-x-2 hover:opacity-80 transition-opacity min-w-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden">
          <UserAvatar
            avatar={creator.avatar}
            name={creator.name}
            gradientClass={avatarGradient}
          />
        </div>
        <div className="min-w-0">
          <div className="font-medium text-content-primary text-sm hover:underline truncate">
            {creator.name || t('creator.nameFallback')}
            {/* Friends-trip surfaced via this friend's membership — they're a
                traveler on it, not its owner. Make that relationship visible. */}
            {creator.matchedVia && (
              <span className="text-content-secondary font-normal">{t('creator.onThisTripSuffix')}</span>
            )}
          </div>
          {timeAgo && (
            <div className="text-xs text-content-secondary">
              {timeAgo}
            </div>
          )}
        </div>
      </Link>

      <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
        {showFollow && creator.canFollow && <FollowButton userId={creator.id} name={creator.name} />}
        {showBadge && <TypeBadge badge={badge} />}
      </div>
    </div>
  );
};

export default memo(CreatorRow);
